// Bottleneck banner — FE-037 / spec §7.0.11.
//
// Renders one of three states:
//   * neutral  — has_bottleneck=false; calm "no bottleneck" copy
//   * warning  — severity=warning; amber accent
//   * critical — severity=critical; red accent
// Errors from the bottleneck query collapse to a quiet warning so a
// hiccup here doesn't break the rest of the CFD section.

import { AlertTriangle, Check, OctagonAlert } from "lucide-react";

import {
  useCfdBottleneckQuery,
  type CfdBottleneckParams,
} from "@/features/dashboard";

import { CFD_STATUS_META } from "./statusMeta";


/** ACQ-008 — the banner mirrors the CFD chart's office / Account slice so
 * a filtered chart is never paired with a whole-book bottleneck verdict. */
export function CfdBottleneckBanner({
  clientFilter, officeFilter,
}: CfdBottleneckParams = {}) {
  const query = useCfdBottleneckQuery({ clientFilter, officeFilter });

  if (query.isLoading) {
    return (
      <div
        data-testid="cfd-bottleneck-loading"
        role="status"
        aria-live="polite"
        className="h-9 animate-pulse rounded-md bg-recessed"
      >
        <span className="sr-only">Checking for workflow bottlenecks…</span>
      </div>
    );
  }

  if (query.isError) {
    return (
      <div
        data-testid="cfd-bottleneck-error"
        role="status"
        className="flex items-center gap-2 rounded-md bg-recessed px-3 py-2 text-sm text-text-secondary"
      >
        <AlertTriangle size={14} aria-hidden="true" />
        <span>Bottleneck check unavailable; CFD data still shown.</span>
      </div>
    );
  }

  const data = query.data;
  if (!data) return null;

  if (!data.has_bottleneck || data.status === null) {
    return (
      <div
        data-testid="cfd-bottleneck-banner"
        data-severity="none"
        role="status"
        className="flex items-center gap-2 rounded-md bg-sage-tint px-3 py-2 text-sm text-text-primary"
      >
        <Check size={14} className="stroke-sage" aria-hidden="true" />
        <span>No workflow bottleneck detected.</span>
      </div>
    );
  }

  const severity = data.severity;
  const tone =
    severity === "critical"
      ? "bg-ember-tint text-ember-ink"
      : "bg-gold-tint text-text-primary";
  const icon =
    severity === "critical" ? (
      <OctagonAlert size={14} aria-hidden="true" />
    ) : (
      <AlertTriangle size={14} aria-hidden="true" />
    );
  const statusLabel = CFD_STATUS_META[data.status].label;

  return (
    <div
      data-testid="cfd-bottleneck-banner"
      data-severity={severity}
      role="alert"
      className={[
        "flex items-start gap-2 rounded-md px-3 py-2 text-sm",
        tone,
      ].join(" ")}
    >
      <span className="mt-0.5 flex-shrink-0">{icon}</span>
      <div className="flex-1">
        <p className="font-medium">
          <span data-testid="cfd-bottleneck-status">{statusLabel}</span>
          {" "}
          grew{" "}
          <span data-testid="cfd-bottleneck-growth">
            {Math.round(data.growth_percent)}%
          </span>
          {" "}week-over-week
          <span className="ml-1 font-normal text-text-secondary">
            ({data.previous_count} → {data.current_count}).
          </span>
        </p>
        {data.message && (
          <p
            data-testid="cfd-bottleneck-message"
            className="mt-0.5 text-xs text-text-secondary"
          >
            {data.message}
          </p>
        )}
      </div>
    </div>
  );
}
